import React, {useMemo, useState} from "react";
import Layout from "../../Shared/Layout";
import {
    InputLabel,
    makeStyles, Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@material-ui/core";
import Collapse from "@mui/material/Collapse";
import styled from "styled-components";
import { Inertia } from "@inertiajs/inertia";
import MuiButton from "@material-ui/core/Button";
import { Field, Form as FinalForm } from "react-final-form";
import TextInput from "../../Shared/TextInput";
import { getCookieValue } from "../../utils/getCookieValue";
import { Trans } from "../../utils/i18n";
import DeleteIcon from "@material-ui/icons/Delete";
import { Alert } from "@material-ui/lab";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Wrapper from "./Wrapper";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {formatCurrency} from "../../utils/formatCurrency";
import FormControl from "@material-ui/core/FormControl";
import {formatPercentage} from "../../utils/formatPercentage";

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 30px;
`;

const HR = styled.hr`
    display: flex;
    justify-content: flex-end;
    margin: 15px 0;
`;

const ResponseWrapper = styled.div`
    width: 100%;
    margin-top: 30px;
`;

const FieldWrapper = styled.div`
    margin-bottom: 10px;
`;

export default function Quotation({
    client,
    order,
    statuses,
    services,
    partners,
    coverages,
    settlements,
    materialOperations,
    files,
    userCanUpdate,
    orderattributes,
    orderhistories,
    extendedMode,
    permissions,
    returnUrl,
    QUOTATION_STATUS_PAID,
    QUOTATION_STATUS_REJECTED,
    QUOTATION_STATUS_PENDING,
    secret,
}) {
    const [formState, setFormState] = useState("");
    const [quotationItems, setQuotationItems] = React.useState([]);
    const [currency, setCurrency] = React.useState('GBP');
    const [_order, setOrder] = useState(order);

    const initialItem = {
        description: "",
        unit_price: 0.0,
        quantity: 1,
        tax: 0,
    };

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [
        showQuotationSaveSuccessMessage,
        setShowQuotationSaveSuccessMessage,
    ] = React.useState(false);
    const [showEmailSentSuccessMessage, setShowEmailSentSuccessMessage] =
        React.useState(false);

    React.useEffect(() => {
        setFormState("initializing");
        fetch("/quotation/" + _order.order_id + "/get_items", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((res) => res.json())
            .then((response) => {
                setQuotationItems(response.data.quotation_items);
                if(response.data.quotation?.currency) {
                    setCurrency(response.data.quotation?.currency);
                }
                setFormState("");
            })
            .catch(() => {
                setFormState("error");
            });
    }, []);

    const {
        taxAmount,
        totalAmount,
    } = useMemo(() => {
        const totalAmount = quotationItems.reduce((total, item) => {
            return total + item.quantity * item.unit_price;
        }, 0);
        const taxAmount = quotationItems.reduce((total, item) => {
            return total + ((item.quantity * item.unit_price) / (100 + item.tax / 100) * item.tax / 100);
        }, 0);
        return {
            taxAmount: taxAmount,
            totalAmount: totalAmount,
        }
    }, [quotationItems]);

    const onSubmitItemModal = async (item, form) => {
        setShowQuotationSaveSuccessMessage(false);
        setShowEmailSentSuccessMessage(false);

        item.quantity = parseInt(item.quantity);
        item.unit_price = parseFloat(item.unit_price.replace(',', '.')) * 100;
        item.tax = parseFloat(item.tax.replace(',', '.')) * 100;

        // Hide Modal
        setIsModalOpen(false);
        // Set Quotation Items
        setQuotationItems([...quotationItems, item]);

        form.restart(initialItem);
    };

    const onSaveQuotation = async () => {
        setShowQuotationSaveSuccessMessage(false);
        setShowEmailSentSuccessMessage(false);
        setFormState("processing");
        await fetch(route("quotation.store"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
                quotation_items: quotationItems,
                order_id: _order.order_id,
                currency: currency,
            }),
        })
            .then((res) => res.json())
            .then((response) => {
                if (response.success) {
                    window.location.reload();
                    // setOrder(response.order);
                    // setShowQuotationSaveSuccessMessage(true);
                    // setTimeout(function () {
                    //     setShowQuotationSaveSuccessMessage(false);
                    // }, 5000);
                    // setFormState("");
                } else {
                    setFormState("error");
                }
            })
            .catch(() => {
                setFormState("error");
            });
    };

    const sendEmail = async () => {
        setShowQuotationSaveSuccessMessage(false);
        setShowEmailSentSuccessMessage(false);
        setFormState("processing");
        await fetch(route("order.quotation.send_email", [_order.order_id]), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                "X-Requested-With": "XMLHttpRequest",
            },
        })
            .then((res) => res.json())
            .then((response) => {
                if (response.success) {
                    setShowEmailSentSuccessMessage(true);
                    setTimeout(function () {
                        setShowEmailSentSuccessMessage(false);
                    }, 5000);
                    setFormState("");
                } else {
                    setFormState("error");
                }
            })
            .catch(() => {
                setFormState("error");
            });
    };

    const isPaid = () => {
        return (
            _order.quotation_payment &&
            _order.quotation_payment.status === "SUCCESS" &&
            _order?.quotation?.status === QUOTATION_STATUS_PAID
        );
    };

    const isRejected = () => {
        return _order?.quotation?.status === QUOTATION_STATUS_REJECTED;
    };

    const isPending = () => {
        return (
            _order.quotation === null ||
            _order?.quotation?.status === QUOTATION_STATUS_PENDING
        );
    };

    const deleteItem = (itemIndex) => {
        const items = Array.from(quotationItems).filter(
            (item, index) => itemIndex !== index
        );

        setQuotationItems(items);
    };

    return (
        <Wrapper>
            <ResponseWrapper>
                {isRejected() && (
                    <Alert severity="info">
                        <Trans i18nKey="queueDetail.quotation.rejectedWith">
                            Quotation rejected with
                        </Trans>
                        <br />
                        <strong>
                            <Trans i18nKey="queueDetail.quotation.rejectionOption">
                                Option
                            </Trans>
                            :
                        </strong>{" "}
                        "{_order?.quotation?.rejection_option}"
                        <br />
                        <strong>
                            <Trans i18nKey="queueDetail.quotation.rejectionReason">
                                Reason
                            </Trans>
                            :
                        </strong>{" "}
                        "{_order?.quotation?.rejection_reason}"
                    </Alert>
                )}
                {isPaid() && (
                    <Alert severity="info">
                        <Trans i18nKey="queueDetail.quotation.quotationIs">
                            Quotation is
                        </Trans>
                        &nbsp;
                        <strong>
                            <Trans i18nKey="queueDetail.quotation.paid">
                                PAID
                            </Trans>
                        </strong>
                    </Alert>
                )}
            </ResponseWrapper>

            <FinalForm initialValues={_order} onSubmit={onSaveQuotation}>
                {({
                    handleSubmit,
                    submitting,
                    values,
                    form,
                    errors,
                    touched,
                }) => {
                    return (
                        <form onSubmit={handleSubmit}>
                            <HR />
                            <h3>
                                <Trans i18nKey="queueDetail.quotation">
                                    Quotation
                                </Trans>
                            </h3>
                            <div className={'flex justify-between'}>
                                {order.permissions.quotation.create &&
                                isPending() && (
                                    <MuiButton
                                        variant="contained"
                                        color={"secondary"}
                                        onClick={() => {
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        +{" "}
                                        <Trans i18nKey="queueDetail.quotation.addItem">
                                            Add Item
                                        </Trans>
                                    </MuiButton>
                                )}
                                {order.permissions.quotation.create &&
                                isPending() && (
                                    <FormControl variant="outlined" className={'max-w-[8rem]'}>
                                        <InputLabel id="filter-client">
                                            <Trans i18nKey="common.currency">
                                                Currency
                                            </Trans>
                                        </InputLabel>
                                        <Select
                                            labelId="filter-client"
                                            label={
                                                <Trans i18nKey="common.currency">
                                                    Currency
                                                </Trans>
                                            }
                                            //value={service}
                                            onChange={(e, val) => {
                                                setCurrency(e.target.value);
                                            }}
                                            value={currency}
                                            native
                                        >
                                            {
                                                //Create array with 1000 items
                                                [
                                                    'GBP',
                                                    'EUR',
                                                    'USD',
                                                ]
                                                    .map((item) => {
                                                        return (
                                                            <option
                                                                key={String(item)}
                                                                value={String(item)}
                                                            >
                                                                {String(item)}
                                                            </option>
                                                        );
                                                    })}
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                            <ResponseWrapper>
                                <Collapse in={showQuotationSaveSuccessMessage}>
                                    <Alert severity="success">
                                        <Trans i18nKey="queueDetail.quotation.saveSuccessfully">
                                            Quotation saved successfully!
                                        </Trans>
                                    </Alert>
                                </Collapse>

                                <Collapse in={showEmailSentSuccessMessage}>
                                    <Alert severity="success">
                                        <Trans i18nKey="queueDetail.quotation.emailSentSuccessfully">
                                            Quotation sent via Email
                                            succesfully!
                                        </Trans>
                                    </Alert>
                                </Collapse>
                            </ResponseWrapper>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Trans i18nKey="queueDetail.quotation.item">
                                                    Item
                                                </Trans>
                                            </TableCell>
                                            <TableCell>
                                                <Trans i18nKey="queueDetail.quotation.unitPrice">
                                                    Unit Price
                                                </Trans>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Trans i18nKey="queueDetail.quotation.quantity">
                                                    Quantity
                                                </Trans>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Trans i18nKey="queueDetail.quotation.taxPercentage">
                                                    Tax(%)
                                                </Trans>
                                            </TableCell>
                                            <TableCell align="left">
                                                <Trans i18nKey="queueDetail.quotation.subtotal">
                                                    Subtotal
                                                </Trans>
                                            </TableCell>
                                            {order.permissions.quotation
                                                .delete &&
                                                isPending() && (
                                                    <TableCell>
                                                        <Trans i18nKey="queueDetail.quotation.actions">
                                                            Actions
                                                        </Trans>
                                                    </TableCell>
                                                )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {quotationItems.map((item, index) => (
                                            <TableRow key={"tableRow" + index}>
                                                <TableCell>
                                                    {item.description}
                                                </TableCell>
                                                <TableCell>
                                                    { formatCurrency(item.unit_price, currency || 'GBP') }
                                                </TableCell>
                                                <TableCell>
                                                    {item.quantity}
                                                </TableCell>
                                                <TableCell>
                                                    { formatPercentage(item.tax) }
                                                </TableCell>
                                                <TableCell>
                                                    { formatCurrency(item.unit_price * item.quantity, currency || 'GBP') }
                                                </TableCell>
                                                {order.permissions.quotation
                                                    .delete &&
                                                    isPending() && (
                                                        <TableCell>
                                                            <DeleteIcon
                                                                onClick={() => {
                                                                    deleteItem(
                                                                        index
                                                                    );
                                                                }}
                                                            />
                                                        </TableCell>
                                                    )}
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={3}></TableCell>
                                            <TableCell>
                                                <Trans i18nKey="queueDetail.quotation.totalWithoutTaxes">
                                                    Total without Taxes
                                                </Trans>
                                            </TableCell>
                                            <TableCell>
                                                { formatCurrency(totalAmount - taxAmount, currency || 'GBP') }
                                            </TableCell>
                                            {order.permissions.quotation
                                                .delete &&
                                                isPending() && (
                                                    <TableCell></TableCell>
                                                )}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={3}></TableCell>
                                            <TableCell>
                                                <Trans i18nKey="queueDetail.quotation.taxAmount">
                                                    Tax Amount
                                                </Trans>
                                            </TableCell>
                                            <TableCell>
                                                { formatCurrency(taxAmount, currency || 'GBP') }
                                            </TableCell>
                                            {order.permissions.quotation
                                                .delete &&
                                                isPending() && (
                                                    <TableCell></TableCell>
                                                )}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={3}></TableCell>
                                            <TableCell>
                                                <Trans i18nKey="queueDetail.quotation.total">
                                                    Total
                                                </Trans>
                                            </TableCell>
                                            <TableCell>
                                                { formatCurrency(totalAmount, currency || 'GBP') }
                                            </TableCell>
                                            {order.permissions.quotation
                                                .delete &&
                                                isPending() && (
                                                    <TableCell></TableCell>
                                                )}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <div className={'flex justify-end gap-3 mt-10'}>
                                {extendedMode && (
                                    <MuiButton
                                        variant="contained"
                                        disabled={submitting}
                                        onClick={() =>
                                            Inertia.visit(
                                                route("queue.index", {
                                                    requesttype:
                                                        _order.requesttype_key,
                                                })
                                            )
                                        }
                                        className={'mr-2'}
                                        style={{ marginLeft: 15 }}
                                    >
                                        <Trans i18nKey="queueDetail.cancel">
                                            Cancel
                                        </Trans>
                                    </MuiButton>
                                )}
                                {isPending() && (
                                    <MuiButton
                                        variant="contained"
                                        color={"secondary"}
                                        disabled={
                                            submitting ||
                                            !order.permissions.quotation
                                                .create ||
                                            formState != "" ||
                                            isPaid()
                                        }
                                        type={"submit"}
                                        onClick={() => {
                                            form.change(
                                                "saveAndCloseJob",
                                                false
                                            );
                                        }}
                                    >
                                        <Trans i18nKey="queueDetail.save">
                                            Save
                                        </Trans>
                                    </MuiButton>
                                )}
                                {_order.quotation &&
                                    order.permissions.quotation.sendEmail && (
                                        <MuiButton
                                            variant="contained"
                                            color={"secondary"}
                                            type={"button"}
                                            disabled={
                                                submitting || formState != ""
                                            }
                                            onClick={() => {
                                                sendEmail();
                                            }}
                                            style={{ marginLeft: 15 }}
                                        >
                                            <Trans i18nKey="queueDetail.quotation.sendEmail">
                                                Send Email
                                            </Trans>
                                        </MuiButton>
                                    )}

                                {_order.quotation &&
                                    order.permissions.quotation.download && (
                                        <MuiButton
                                            variant="contained"
                                            color={"secondary"}
                                            type={"button"}
                                            disabled={
                                                submitting || formState != ""
                                            }
                                            href={route(
                                                "quotation.generate_pdf",
                                                {
                                                    secret,
                                                    filename: `quotation-${order.number}.pdf`,
                                                }
                                            )}
                                            target="_blank"
                                            style={{ marginLeft: 15 }}
                                        >
                                            <Trans i18nKey="queueDetail.quotation.downloadAs">
                                                Download as
                                            </Trans>
                                            <PictureAsPdfIcon />
                                        </MuiButton>
                                    )}

                                <br />
                            </div>
                        </form>
                    );
                }}
            </FinalForm>

            {order.permissions.quotation.create && (
                <FinalForm
                    initialValues={initialItem}
                    onSubmit={onSubmitItemModal}
                >
                    {({
                        handleSubmit,
                        submitting,
                        values,
                        form,
                        errors,
                        touched,
                    }) => {
                        return (
                            <Dialog
                                open={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                            >
                                <form onSubmit={handleSubmit}>
                                    <DialogTitle>
                                        <Trans i18nKey="queueDetail.quotation.addItem">
                                            Add Item
                                        </Trans>
                                    </DialogTitle>
                                    <DialogContent>
                                        <FieldWrapper>
                                            <Field
                                                name={"description"}
                                                component={TextInput}
                                                field={{
                                                    label: (
                                                        <Trans i18nKey="queueDetail.quotation.itemDescription">
                                                            Item Description
                                                        </Trans>
                                                    ),
                                                    type: "text",
                                                    size: "small",
                                                }}
                                                validate={(value) =>
                                                    value
                                                        ? undefined
                                                        : "Required"
                                                }
                                            />
                                        </FieldWrapper>
                                        <FieldWrapper>
                                            <Field
                                                name={"unit_price"}
                                                component={TextInput}
                                                field={{
                                                    label: (
                                                        <Trans i18nKey="queueDetail.quotation.unitPrice">
                                                            Unit Price
                                                        </Trans>
                                                    ),
                                                    type: "text",
                                                    size: "small",
                                                }}
                                                validate={(value) =>
                                                    parseFloat(value) >
                                                    0 ? undefined : (
                                                        <Trans i18nKey="queueDetail.quotation.unitPriceErrGt0">
                                                            Must be greater than
                                                            0
                                                        </Trans>
                                                    )
                                                }
                                            />
                                        </FieldWrapper>
                                        <FieldWrapper>
                                            <Field
                                                name={"quantity"}
                                                component={TextInput}
                                                field={{
                                                    label: (
                                                        <Trans i18nKey="queueDetail.quotation.quantity">
                                                            Quantity
                                                        </Trans>
                                                    ),
                                                    type: "text",
                                                    size: "small",
                                                }}
                                            />
                                        </FieldWrapper>
                                        <FieldWrapper>
                                            <Field
                                                name={"tax"}
                                                component={TextInput}
                                                field={{
                                                    label: (
                                                        <Trans i18nKey="queueDetail.quotation.taxPercentage">
                                                            Tax(%)
                                                        </Trans>
                                                    ),
                                                    type: "text",
                                                    size: "small",
                                                    input: {
                                                        step: 1,
                                                        min: 0,
                                                    },
                                                }}
                                            />
                                        </FieldWrapper>
                                    </DialogContent>
                                    <DialogActions>
                                        <MuiButton
                                            variant="contained"
                                            onClick={() =>
                                                setIsModalOpen(false)
                                            }
                                            className={'mr-2'}
                                            style={{ marginLeft: 15 }}
                                        >
                                            <Trans i18nKey="queueDetail.cancel">
                                                Cancel
                                            </Trans>
                                        </MuiButton>
                                        <MuiButton
                                            variant="contained"
                                            color={"secondary"}
                                            type={"submit"}
                                        >
                                            <Trans i18nKey="queueDetail.quotation.addItem">
                                                Add Item
                                            </Trans>
                                        </MuiButton>
                                    </DialogActions>
                                </form>
                            </Dialog>
                        );
                    }}
                </FinalForm>
            )}
        </Wrapper>
    );
}

Quotation.layout = (page) => <Layout children={page} />;
